import { atom, type Atom, type PrimitiveAtom } from 'jotai';
import { type IEnvironment } from 'relay-runtime';

import { withRequestTiming } from '../../common/analytics/withRequestTiming';
import { requestKnowledgeSources } from '../../utils/appConnectors';
import { requestAvailableProducts } from '../../utils/availableProducts';
import {
	requestCollaborationUsers,
	type CollabGraphRecommendedEntity,
} from '../../utils/collaborationUsers';
import { requestGlobalMetrics, type GlobalMetricsResponse } from '../../utils/globalMetrics';
import {
	requestOrgMetrics,
	type NoDataResponse,
	type OrgMetricsResponse,
} from '../../utils/orgMetrics';
import {
	requestUserMetrics,
	isUserNoDataResponse,
	type UserMicrositeHeroMetricsResponse,
} from '../../utils/userMetrics';
import {
	STATIC_COLLABORATION_USERS,
	STATIC_TIER2_DATASETS,
	STATIC_TIER1_DATASETS,
	STATIC_TIER3_DATASETS,
	type DebugTier,
	type StaticTier2DatasetKey,
	type StaticTier1DatasetKey,
	type StaticTier3DatasetKey,
} from '../components/DebugPanel/staticDatasets';
import type { Tab as GettingStartedTab } from '../components/Sections/GettingStartedSection/TabToggle';
import type { AvailableSite } from '../components/SiteSwitcher/types';
import { SiteSelectionSource } from '../components/SiteSwitcher/types';

/**
 * Debug toggle to preview the logged-out view while authenticated.
 * When true, Home renders LoggedOutView instead of LoggedInView.
 * Only intended for development/preview purposes.
 */
export const forceLoggedOutViewAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(false);

/**
 * Selects how `OrgMetricsAtom` sources its payload.
 *
 * - `'api'` (default): fetch from the live upstream endpoints (e.g.
 *   `metrics/org/{orgId}` for `OrgMetricsAtom`, `recommend/user` for
 *   `CollaborationUsersAtom`) using the live tenant atoms.
 * - `'static'`: short-circuit the network call and serve the canned
 *   payload bundled with the debug panel. For `OrgMetricsAtom` this is
 *   the dataset identified by `selectedStaticTier2DatasetKeyAtom`; for
 *   `CollaborationUsersAtom` it is the shared `STATIC_COLLABORATION_USERS`
 *   roster (the same five users for every dataset).
 *
 * Only intended for development/preview purposes via the debug panel.
 */
export type OrgMetricsSource = 'api' | 'static';

/**
 * Writable atom controlling which source the debug-panel-aware atoms
 * (`OrgMetricsAtom`, `CollaborationUsersAtom`) read from. Defaults to
 * `'api'` so the production logged-in flow is unchanged when the debug
 * panel has not been touched.
 */
export const orgMetricsSourceAtom: PrimitiveAtom<OrgMetricsSource> & {
	init: OrgMetricsSource;
} = atom<OrgMetricsSource>('api');

/**
 * Writable atom holding which debug-panel tier is currently active.
 * Selecting a tier in the debug panel forces `LoggedInView` to render
 * the corresponding view (Tier 1 → `UserGraphView`, Tier 2 →
 * `OrgGraphView`, Tier 3 → `GettingStartedView`) regardless of the
 * live tier-gating logic.
 *
 * Defaults to `'tier2'` so existing static datasets continue to route
 * to `OrgGraphView` when the debug panel has not been touched.
 */
export const selectedDebugTierAtom: PrimitiveAtom<DebugTier> & {
	init: DebugTier;
} = atom<DebugTier>('tier1');

/**
 * Writable atom holding the currently selected static *Tier 1* dataset
 * key for the debug panel. `null` means "no Tier 1 dataset chosen yet".
 * When `orgMetricsSourceAtom` is `'static'`, `selectedDebugTierAtom` is
 * `'tier1'`, and this is non-null, `UserMetricsAtom` resolves
 * synchronously to the matching `UserMicrositeHeroMetricsResponse`
 * payload from `STATIC_TIER1_DATASETS`.
 */
export const selectedStaticTier1DatasetKeyAtom: PrimitiveAtom<StaticTier1DatasetKey | null> & {
	init: StaticTier1DatasetKey | null;
} = atom<StaticTier1DatasetKey | null>(null);

/**
 * Writable atom holding the currently selected static *Tier 2* dataset
 * key for the debug panel. `null` means "no Tier 2 dataset chosen yet".
 * When `orgMetricsSourceAtom` is `'static'`, `selectedDebugTierAtom` is
 * `'tier2'`, and this is non-null:
 * - `OrgMetricsAtom` resolves synchronously to the matching
 *   `OrgMetricsResponse` payload from `STATIC_TIER2_DATASETS`.
 * - `CollaborationUsersAtom` resolves synchronously to the shared
 *   `STATIC_COLLABORATION_USERS` roster (the same roster regardless of
 *   which dataset key is selected).
 */
export const selectedStaticTier2DatasetKeyAtom: PrimitiveAtom<StaticTier2DatasetKey | null> & {
	init: StaticTier2DatasetKey | null;
} = atom<StaticTier2DatasetKey | null>(null);

/**
 * Writable atom holding the currently selected static *Tier 3* dataset
 * key for the debug panel. `null` means "no Tier 3 dataset chosen yet".
 * Tier 3 datasets are label/description pairs only — the capability
 * showcase view does not read numeric data from them — so this atom
 * exists purely to gate whether the debug panel has forced a Tier 3
 * preview and to remember the user's selection across re-renders.
 */
export const selectedStaticTier3DatasetKeyAtom: PrimitiveAtom<StaticTier3DatasetKey | null> & {
	init: StaticTier3DatasetKey | null;
} = atom<StaticTier3DatasetKey | null>(null);

/**
 * Writable atom that holds the current cloud ID.
 *
 * Initialised to `null` to represent the "not yet known" state. The real
 * cloud ID is hydrated from the response of the `/gateway/api/app-switcher`
 * `available-products` endpoint (see the `availableSitesAtom` /
 * `LoggedInGate` flow). Async atoms that depend on this value (e.g.
 * `KnowledgeSourcesAtom`, `CollaborationUsersAtom`) early-return an empty
 * idle result while it is `null`, so no network calls are made with an
 * unknown tenant.
 *
 * This atom can be updated at runtime — for example, when the user
 * switches tenants via the `SiteSwitcher` — by calling `set` on it.
 */
export const cloudIdAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

/**
 * Writable atom that holds the current org ID.
 *
 * The initial value is derived from `getConfig().orgId`, which
 * resolves based on the current environment (staging, production, etc.).
 *
 * This atom can be updated at runtime — for example, when the user
 * switches tenants via an app-switcher — by calling `set` on it.
 */
export const orgIdAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

export const previousSelectionSourceAtom: PrimitiveAtom<SiteSelectionSource> & {
	init: SiteSelectionSource;
} = atom<SiteSelectionSource>(SiteSelectionSource.Default);

/**
 * Writable atom that holds the current user's Atlassian account ID.
 *
 * Initialised as an empty string and populated by `LoggedInView` once
 * the `UserProvider` has resolved the authenticated user. Modelled as
 * a Jotai atom (rather than read directly from React context) so that
 * async atoms which depend on the account ID — such as
 * `CollaborationUsersAtom` — can be evaluated alongside other data
 * fetching atoms (e.g. `KnowledgeSourcesAtom`) without needing to be
 * wired through React state.
 */
export const accountIdAtom: PrimitiveAtom<string> & {
	init: string;
} = atom<string>('');

/**
 * Holds the active Relay `IEnvironment` so async atoms can issue GraphQL
 * queries without needing to live inside a React component.
 *
 * Hydrated once by `LoggedInView` via `useHydrateAtoms` from the
 * environment provided by `@atlassian/relay-environment-provider`. Atoms
 * that depend on this should treat a `null` value as "not ready yet" and
 * return an empty/idle result instead of firing a network request.
 */
export const relayEnvironmentAtom: PrimitiveAtom<IEnvironment | null> & {
	init: IEnvironment | null;
} = atom<IEnvironment | null>(null);

/**
 * Result type returned by `availableSitesAtom`.
 *
 * On success, `data` is the list of sites the user can access (used by
 * `SiteSwitcher` for rendering and by `LoggedInGate` for resolving the
 * initial `cloudId`). On failure, `data` is empty and `error` carries
 * the message; consumers can fall back gracefully without crashing.
 */
export type AvailableSitesResult = {
	data: AvailableSite[];
	error: string | null;
};

/**
 * Derived async atom that fetches the list of Atlassian sites/products
 * available to the current user via the App Switcher gateway endpoint.
 *
 * This atom is intentionally evaluated *before* `LoggedInView` mounts,
 * so the `LoggedInGate` wrapper can pick the correct `cloudId` from the
 * response and hydrate `cloudIdAtom` with it. Downstream atoms that
 * depend on `cloudIdAtom` (e.g. `KnowledgeSourcesAtom`,
 * `CollaborationUsersAtom`) early-return an idle result while
 * `cloudIdAtom` is `null`, ensuring no network calls are made with an
 * unknown tenant.
 *
 * Never throws — on failure, returns `{ data: [], error: "..." }` so
 * consumers can fall back gracefully.
 *
 * Components using this atom will suspend (React Suspense) until the
 * data is resolved. Wrap the consuming subtree in a
 * `<Suspense fallback={...}>` boundary.
 */
export const availableSitesAtom: Atom<Promise<AvailableSitesResult>> = atom<
	Promise<AvailableSitesResult>
>(async () => {
	try {
		const { execute } = requestAvailableProducts();
		const response = await withRequestTiming(
			'operational.availableProductsRequest.completed.twgMicrositeAvailableProducts',
			execute,
		);
		if (response.success) {
			return { data: response.data.sites, error: null };
		}
		return {
			data: [],
			error: `Failed to fetch available products: ${response.error?.message ?? response.error}`,
		};
	} catch (error) {
		return {
			data: [],
			error: error instanceof Error ? error.message : 'An unexpected error occurred',
		};
	}
});

/**
 * Result type returned by KnowledgeSourcesAtom.
 * On success, `data` contains the sources and `error` is null.
 * On failure, `data` is an empty array and `error` holds the message.
 */
export type AppConnector = {
	displayName: string;
	iconUrl: string;
	outboundAuthUrl: string;
	isUserOAuthed: boolean;
};

export type AppConnectorResults = {
	data: AppConnector[];
	error: string | null;
};

/**
 * Derived async atom that fetches knowledge sources based on the current
 * cloud ID. The data is sourced from the rovo
 * `/gateway/api/assist/rovo/v1/agents/configuration/knowledge` REST endpoint
 * via `requestKnowledgeSources`, which is treated as the source of truth
 * for the connector list as well as their display metadata (name, icon,
 * outbound auth URL, OAuth state).
 *
 * Because it reads from `cloudIdAtom`, Jotai will automatically re-fetch
 * whenever it changes (e.g. when the user switches tenants).
 *
 * Never throws — on failure, returns `{ data: [], error: "..." }` so
 * consumers can handle the error gracefully without an ErrorBoundary.
 *
 * Components using this atom will suspend (React Suspense) until
 * the data is resolved. Wrap the consuming component in a
 * `<Suspense fallback={...}>` boundary.
 */
export const KnowledgeSourcesAtom: Atom<Promise<AppConnectorResults>> = atom<
	Promise<AppConnectorResults>
>(async (get) => {
	const cloudId = get(cloudIdAtom);

	if (!cloudId) {
		return { data: [], error: null };
	}

	try {
		const rovoRequest = requestKnowledgeSources(cloudId);
		const rovoResponse = await withRequestTiming(
			'operational.appConnectorsRequest.completed.twgMicrositeAppConnectors',
			rovoRequest.execute,
			{ requestName: 'knowledgeSources' },
		);

		if (!rovoResponse.success) {
			return {
				data: [],
				error: `Failed to fetch connectors: ${rovoResponse.error?.message ?? rovoResponse.error}`,
			};
		}

		const rovoConnectors = rovoResponse.data.knowledgeSources;

		const finalParsedConnectors: AppConnector[] = [];
		const seenConnectorKeys = new Set<string>();

		rovoConnectors.forEach((connector) => {
			const parsedConnector: AppConnector = {
				displayName: connector.friendlyName,
				iconUrl: connector.iconUrl,
				outboundAuthUrl: connector.outboundAuthUrl,
				isUserOAuthed: connector.isUserOAuthed,
			};

			const connectorKey = `${parsedConnector.displayName}::${parsedConnector.iconUrl}::${parsedConnector.outboundAuthUrl ?? ''}`;
			if (seenConnectorKeys.has(connectorKey)) {
				return;
			}

			seenConnectorKeys.add(connectorKey);
			finalParsedConnectors.push(parsedConnector);
		});

		return {
			data: finalParsedConnectors,
			error: null,
		};
	} catch (error) {
		return {
			data: [],
			error: error instanceof Error ? error.message : 'An unexpected error occurred',
		};
	}
});

/**
 * Result type returned by CollaborationUsersAtom.
 * On success, `data` contains the recommended collaboration users and
 * `error` is null. On failure, `data` is an empty array and `error`
 * holds the message so consumers can fall back gracefully.
 */
export type CollaborationUsersResult = {
	data: CollabGraphRecommendedEntity[];
	error: string | null;
};

/**
 * Derived async atom that fetches the user's top collaboration users
 * based on the current cloud ID and account ID.
 *
 * Because it reads from `cloudIdAtom` and `accountIdAtom`, Jotai will
 * evaluate this atom at the same time as `KnowledgeSourcesAtom` (both
 * are kicked off when their consuming components first mount under the
 * shared Suspense boundary in `Home`). It will also automatically
 * re-fetch whenever either dependency changes.
 *
 * If the cloud ID or account ID is not yet available, returns an empty
 * result without hitting the network.
 *
 * Debug panel override: when `orgMetricsSourceAtom` is `'static'` and a
 * dataset has been picked via `selectedStaticTier2DatasetKeyAtom`, the network
 * call is short-circuited and the shared `STATIC_COLLABORATION_USERS`
 * roster is returned instead — the same five users for every dataset.
 *
 * Never throws — on failure, returns `{ data: [], error: "..." }` so
 * consumers can handle the error gracefully without an ErrorBoundary.
 *
 * Components using this atom will suspend (React Suspense) until the
 * data is resolved. Wrap the consuming component in a
 * `<Suspense fallback={...}>` boundary.
 */
export const CollaborationUsersAtom: Atom<Promise<CollaborationUsersResult>> = atom<
	Promise<CollaborationUsersResult>
>(async (get) => {
	const source = get(orgMetricsSourceAtom);

	// Debug panel override: when a static dataset has been picked for the
	// active tier, serve the canned collaborator roster directly without
	// touching the network. The same roster is returned for every
	// dataset (and every tier) so demo flows always show a recognisable
	// Teamwork Graph team. Tier 3 is a pure capability showcase that
	// doesn't render collaborators, so the roster is still returned but
	// ignored downstream.
	if (source === 'static') {
		const tier = get(selectedDebugTierAtom);
		const hasSelection =
			(tier === 'tier1' && get(selectedStaticTier1DatasetKeyAtom) !== null) ||
			(tier === 'tier2' && get(selectedStaticTier2DatasetKeyAtom) !== null) ||
			(tier === 'tier3' && get(selectedStaticTier3DatasetKeyAtom) !== null);
		if (hasSelection) {
			return { data: [...STATIC_COLLABORATION_USERS], error: null };
		}
		return { data: [], error: null };
	}

	const cloudId = get(cloudIdAtom);
	const accountId = get(accountIdAtom);

	if (!cloudId || !accountId) {
		return { data: [], error: null };
	}

	try {
		const { execute } = requestCollaborationUsers({
			requestingUserId: accountId,
			targetUserId: accountId,
			tenantId: cloudId,
			limit: 5,
		});
		const response = await withRequestTiming(
			'operational.collaborationUsersRequest.completed.twgMicrositeCollaborationUsers',
			execute,
		);
		if (response.success) {
			return { data: response.data.recommendedEntities, error: null };
		}
		return {
			data: [],
			error: `Failed to fetch collaboration users: ${response.error?.message ?? response.error}`,
		};
	} catch (error) {
		return {
			data: [],
			error: error instanceof Error ? error.message : 'An unexpected error occurred',
		};
	}
});

/**
 * Result type returned by GlobalMetricsAtom.
 * On success, `data` contains the platform-wide metrics row and `error` is null.
 * On failure, `data` is `null` and `error` holds the message so consumers can
 * fall back to the existing hard-coded placeholder strings.
 */
export type GlobalMetricsResult = {
	data: GlobalMetricsResponse | null;
	error: string | null;
};

/**
 * Derived async atom that fetches the unauthenticated platform-wide hero
 * metrics from `/twg-api/v1/microsite/metrics/global`.
 *
 * The endpoint is anonymous and edge-cached at the CDN for 15 minutes, so this
 * atom has no React/Jotai dependencies and only fires once per page load. It
 * is consumed by `Stats` (rendered in the logged-out home view) under the
 * Suspense boundary in `LoggedOutView`.
 *
 * Never throws — on failure, returns `{ data: null, error: "..." }` so
 * consumers can fall back gracefully without an ErrorBoundary.
 */
export const GlobalMetricsAtom: Atom<Promise<GlobalMetricsResult>> = atom<
	Promise<GlobalMetricsResult>
>(async () => {
	try {
		const { execute } = requestGlobalMetrics();
		const response = await withRequestTiming(
			'operational.globalMetricsRequest.completed.twgMicrositeGlobalMetrics',
			execute,
		);
		if (response.success) {
			return { data: response.data, error: null };
		}
		return {
			data: null,
			error: `Failed to fetch global metrics: ${response.error?.message ?? response.error}`,
		};
	} catch (error) {
		return {
			data: null,
			error: error instanceof Error ? error.message : 'An unexpected error occurred',
		};
	}
});

/**
 * Result type returned by OrgMetricsAtom.
 *
 * On success, `data` contains either the full org-scoped metrics payload or a
 * `NoDataResponse` sentinel when the upstream dbt model has not yet produced
 * rows for the org; `error` is null in both cases. On failure, `data` is
 * `null` and `error` holds the message so consumers can fall back to the
 * existing hard-coded placeholder strings.
 *
 * Consumers should narrow on `data.status === 'NO_DATA'` to render an
 * empty-state separately from the populated metrics view.
 */
export type OrgMetricsResult = {
	data: OrgMetricsResponse | NoDataResponse | null;
	error: string | null;
};

/**
 * Derived async atom that fetches the authenticated org-scoped hero metrics
 * from `/twg-api/v1/microsite/metrics/org/{orgId}`.
 *
 * Because it reads from `orgIdAtom` and `cloudIdAtom`, Jotai will
 * automatically re-fetch whenever either dependency changes (e.g. when the
 * user switches tenants). It is consumed by the logged-in home view under a
 * shared Suspense boundary alongside `KnowledgeSourcesAtom` and
 * `CollaborationUsersAtom`.
 *
 * If the org ID or cloud ID is not yet available, returns an empty result
 * without hitting the network.
 *
 * Never throws — on failure, returns `{ data: null, error: "..." }` so
 * consumers can fall back gracefully without an ErrorBoundary.
 */
export const OrgMetricsAtom: Atom<Promise<OrgMetricsResult>> = atom<Promise<OrgMetricsResult>>(
	async (get) => {
		const source = get(orgMetricsSourceAtom);

		// Debug panel override: when the source is `'static'` and a Tier 2
		// dataset has been picked, serve it directly without touching the
		// network. The payload conforms to `OrgMetricsResponse`, so
		// downstream consumers (e.g. `selectMetrics`) treat it identically
		// to a live response. Tier 1 and Tier 3 previews intentionally do
		// not seed org metrics — Tier 1 drives `UserMetricsAtom` instead,
		// and Tier 3 is a pure capability showcase that ignores numeric
		// data — so we fall through to the null payload in those cases.
		if (source === 'static') {
			const tier = get(selectedDebugTierAtom);

			if (tier === 'tier2') {
				const datasetKey = get(selectedStaticTier2DatasetKeyAtom);
				if (datasetKey) {
					return {
						data: STATIC_TIER2_DATASETS[datasetKey].metrics,
						error: null,
					};
				}
			}
			if (tier === 'tier3') {
				const datasetKey = get(selectedStaticTier3DatasetKeyAtom);
				if (datasetKey) {
					return {
						data: STATIC_TIER3_DATASETS[datasetKey].metrics,
						error: null,
					};
				}
			}

			return { data: null, error: null };
		}

		const orgId = get(orgIdAtom);
		const cloudId = get(cloudIdAtom);
		const accountId = get(accountIdAtom);

		if (!orgId || !cloudId) {
			return { data: null, error: null };
		}

		try {
			const { execute } = requestOrgMetrics(orgId, cloudId, accountId);
			const response = await withRequestTiming(
				'operational.orgMetricsRequest.completed.twgMicrositeOrgMetrics',
				execute,
			);
			if (response.success) {
				if ('status' in response.data && response.data.status === 'NO_DATA') {
					return { data: null, error: null };
				}

				return { data: response.data, error: null };
			}
			return {
				data: null,
				error: `Failed to fetch org metrics: ${response.error?.message ?? response.error}`,
			};
		} catch (error) {
			return {
				data: null,
				error: error instanceof Error ? error.message : 'An unexpected error occurred',
			};
		}
	},
);

/**
 * Tracks which install-instructions tab is currently selected in the
 * Getting Started section ('cli' or 'mcp'). Hoisted to a module-level
 * jotai atom (instead of `useState` inside `GettingStartedSection`) so
 * external triggers — most notably the "CLI" nav button in
 * `NavButtons` — can both switch the tab *and* smooth-scroll the user
 * down to the section in the same click.
 *
 * Defaults to `'cli'` to match the previous local-state default.
 */
export const gettingStartedSelectedTabAtom: PrimitiveAtom<GettingStartedTab> & {
	init: GettingStartedTab;
} = atom<GettingStartedTab>('cli');

/**
 * Result type returned by UserMetricsAtom.
 *
 * `data` is the full user-scoped metrics payload on success, or null on
 * NO_DATA (404), missing orgId, or static source. `error` is non-null only
 * on network/parse failures.
 */
export type UserMetricsResult = {
	data: UserMicrositeHeroMetricsResponse | null;
	error: string | null;
};

/**
 * Derived async atom that fetches the authenticated user-scoped hero metrics
 * from `/twg-api/v1/microsite/metrics/user?organisationId={orgId}`.
 *
 * **Sequencing:** This atom reads `CollaborationUsersAtom` and waits for it
 * to resolve before issuing the request, so the resolved collaborator ARIs
 * can be forwarded as the `X-Top-Collaborators` header. The Flock-backed
 * endpoint uses that header to populate the optional `workByCollaborators`
 * field with shared-work entities per collaborator. If the collaborator
 * fetch failed or returned no users, the request is still made (without
 * the header) so the rest of the metrics payload is still rendered.
 *
 * Mirrors the shape of OrgMetricsAtom:
 * - Returns { data: null, error: null } when source is 'static' (Tier 1 seeds
 *   the canned payload directly; Tier 2/3 leave user metrics empty)
 * - Returns { data: null, error: null } when orgId is unavailable
 * - On 404 (NO_DATA), normalises to { data: null, error: null }
 * - On success, returns { data: UserMicrositeHeroMetricsResponse, error: null }
 * - On failure, returns { data: null, error: '...' } — never throws
 */
export const UserMetricsAtom: Atom<Promise<UserMetricsResult>> = atom<Promise<UserMetricsResult>>(
	async (get) => {
		const source = get(orgMetricsSourceAtom);

		// Debug panel override: when the source is `'static'` and a Tier 1
		// dataset has been picked, serve the matching user-metrics payload
		// directly. Tier 2 and Tier 3 previews do not populate user
		// metrics — Tier 2 routes to `OrgGraphView`, and Tier 3 is a
		// capability showcase that ignores numeric data — so we fall
		// through to the null payload in those cases.
		if (source === 'static') {
			const tier = get(selectedDebugTierAtom);
			if (tier === 'tier1') {
				const tier1Key = get(selectedStaticTier1DatasetKeyAtom);
				if (tier1Key) {
					return { data: STATIC_TIER1_DATASETS[tier1Key].metrics, error: null };
				}
			}
			return { data: null, error: null };
		}

		const orgId = get(orgIdAtom);
		const cloudId = get(cloudIdAtom);

		if (!orgId || !cloudId) {
			return { data: null, error: null };
		}

		// Wait for the collaboration-graph fetch to resolve before issuing
		// the user-metrics request. The Flock endpoint uses the resolved
		// collaborator ARIs as the `X-Top-Collaborators` header to populate
		// the optional `workByCollaborators` field. We tolerate empty /
		// failed collaborator results by still firing the request without
		// the header — the rest of the metrics payload is still useful.
		const collaborationUsers = await get(CollaborationUsersAtom);
		const topCollaboratorAris = collaborationUsers.data
			.map((entity) => entity.expand?.ari)
			.filter((ari): ari is string => typeof ari === 'string' && ari.length > 0);

		try {
			const { execute } = requestUserMetrics(orgId, cloudId, topCollaboratorAris);
			const response = await withRequestTiming(
				'operational.userMetricsRequest.completed.twgMicrositeUserMetrics',
				execute,
			);
			if (response.success) {
				if (isUserNoDataResponse(response.data)) {
					return { data: null, error: null };
				}
				return { data: response.data as UserMicrositeHeroMetricsResponse, error: null };
			}
			return {
				data: null,
				error: `Failed to fetch user metrics: ${response.error?.message ?? response.error}`,
			};
		} catch (error) {
			return {
				data: null,
				error: error instanceof Error ? error.message : 'An unexpected error occurred',
			};
		}
	},
);
